import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface Palette {
    system: {
      red: string;
      redWarning: string;
      blue: string;
      green: string;
    };
  }
}

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#FAFAFA',
  200: '#EEEEEE',
  300: '#E0E0E0',
  400: '#BDBDBD',
  500: '#9E9E9E',
  600: '#616161',
  700: '#454F5B',
  800: '#202123',
  900: '#161C24',
};

const PRIMARY = {
  lighter: '#A8DFFF',
  light: '#6CA7F2',
  main: '#2BB1E6',
  dark: '#005EA2',
  darker: '#00366F',
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#9C99AF',
  light: '#737085',
  main: '#5F5C71',
  dark: '#403E51',
  darker: '#1F1E2F',
  contrastText: '#fff',
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#D8FBDE',
  light: '#86E8AB',
  main: '#36B37E',
  dark: '#1B806A',
  darker: '#0A5554',
  contrastText: '#fff',
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: '#fff',
};

const ERROR = {
  lighter: '#FFB7BE',
  light: '#FF827E',
  main: '#FF5449',
  dark: '#FF1400',
  darker: '#F00007',
  contrastText: '#fff',
};

const SYSTEM = {
  red: '#F93D40', // 흡연/실패 이벤트 (CravingHistory smokeYn, CravingEndButton)
  redWarning: '#F44336', // 경고/주의 강조 (미션 미완료, 고위험 시간대, D-day 등)
  blue: '#008FF8',
  green: '#50B18A',
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
  system: SYSTEM,
};

export default function palette(themeMode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#fff',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.16),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  } as const;

  return themeMode === 'light' ? light : dark;
}
